import { logError } from "@entrilia/shared/logging";
import type {
  ConditionConfiguration,
  FieldConfiguration,
} from "../../../../../../api/types/fieldConfigurationTypes.ts";
import { FieldConfigurationType } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ChartStyleSettings,
  ConditionField,
  FinancialStatementGeneralSettings,
  ListGeneralSettings,
  PivotGeneralSettings,
  ReportConfiguration,
  ReportField,
  SortableField,
  SortConfiguration,
  SortField,
  TabularGeneralSettings,
  TileSettings,
} from "../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../api/types/svTypes.ts";
import { configurationToConditions } from "../../components/builder/utils/configuration/configurations";
import { chartRelatedReportTypes } from "../../utilities/chartRelatedReport.types.ts";
import { isMeasureConfigurationValid } from "../../utilities/isConfigurationValid.ts";
import { isListReportType, usesTabularConfiguration, isTileReportType } from "../../utilities/reportTypeHelper.ts";
import type { NoDuplicateKeys } from "../../utilities/typesHelper";
import { selectCurrentReport } from "../selectors/currentReportSelectors";
import {
  selectBuilderDetailedView,
  selectColumns,
  selectConditions,
  selectListFieldsState,
  selectReportType,
  selectRows,
  selectTabularFields,
} from "../selectors/fieldsStateSelectors";
import { selectAggregations, selectDimensions, selectMeasures, selectMetrics } from "../selectors/metaDataSelectors";
import type { RootState } from "../types/store.types.ts";
import { createTypedAsyncThunk } from "../utils/createTypedAsyncThunk";
import { setFieldStatesConfiguration } from "../utils/fieldsStateSliceHelper";
import { withListDateEqualityDefault, withoutListDateEquality } from "../utils/listConditionsHelper";
import { createListLedgerChange } from "../utils/listLedgerChange.ts";
import {
  createChartAction,
  createCurrentReportAction,
  createFieldsStateAction,
  createFinancialStatementAction,
  createListAction,
  createPivotAction,
  createTabularAction,
  createTileAction,
} from "../utils/storeUtils";
import { chartFieldsStateThunks } from "./chartFieldsStateThunks";
import { pivotFieldsStateThunks } from "./pivotFieldsStateThunks";
import { tabularFieldsStateThunks } from "./tabularFieldsStateThunks";

const initializeFieldsState = createTypedAsyncThunk(
  "fieldsState/initializeFieldsState",
  (
    data: {
      configuration: ReportConfiguration;
      reportType: ReportType;
    },
    { dispatch, getState }
  ) => {
    try {
      const { configuration, reportType } = data;

      const state = getState();
      dispatch(createFieldsStateAction("setReportType", reportType));
      const dimensions = selectDimensions(state);
      const measures = selectMeasures(state);
      const metrics = selectMetrics(state);
      const aggregations = selectAggregations(state);
      setFieldStatesConfiguration(dimensions, measures, metrics, configuration, reportType, dispatch, aggregations);
      dispatch(createCurrentReportAction("refreshSession"));
    } catch (error) {
      logError(error, "[initializeFieldsState]");
    }
  }
);

export const resetFieldsState = createTypedAsyncThunk(
  "fieldsState/resetFieldsState",
  (reportType: ReportType | undefined, { dispatch }) => {
    dispatch(createTabularAction("resetToDefaultAction"));

    dispatch(createPivotAction("resetToDefaultAction", undefined));

    dispatch(createChartAction("resetToDefaultAction", reportType));

    dispatch(createTileAction("resetToDefaultAction"));

    dispatch(createListAction("resetToDefaultAction"));

    dispatch(createFinancialStatementAction("resetToDefaultAction"));
  }
);

const clearCurrentConfiguration = createTypedAsyncThunk(
  "fieldsState/clearCurrentConfiguration",
  (_, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("resetToDefaultAction", undefined));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("resetToDefaultAction"));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("resetToDefaultAction", reportType));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("resetToDefaultAction"));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("resetToDefaultAction"));
    } else if (reportType === ReportType.FinancialStatement) {
      dispatch(createFinancialStatementAction("resetToDefaultAction"));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const handleReportTypeChange = createTypedAsyncThunk(
  "fieldsState/handleReportTypeChange",
  (reportType: ReportType, { dispatch, getState }) => {
    const state = getState();
    const currentReport = selectCurrentReport(state);
    const currentConditions = selectConditions(state);
    const detailedView = selectBuilderDetailedView(state);
    const dimensions = selectDimensions(state);

    const conditions = currentConditions.map((c) => c.config) || (currentReport?.configuration.conditions ?? []);
    dispatch(resetFieldsState(reportType));
    dispatch(createFieldsStateAction("setReportType", reportType));

    dispatch(updateSettings({ settings: { detailedView } }));
    const result = configurationToConditions(conditions, dimensions);
    dispatch(
      setConditions(
        reportType === ReportType.List ? result.map(withListDateEqualityDefault) : result.map(withoutListDateEquality)
      )
    );

    dispatch(createCurrentReportAction("refreshSession"));
  }
);

const setConditions = createTypedAsyncThunk<void, ConditionField[]>(
  "fields/setConditions",
  (conditions, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("setConditions", conditions));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("setConditions", conditions));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("setConditions", conditions));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("setConditions", conditions));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("setConditions", conditions));
    } else if (reportType === ReportType.FinancialStatement) {
      dispatch(createFinancialStatementAction("setConditions", conditions));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const addCondition = createTypedAsyncThunk<void, { field: ConditionField; index: number }>(
  "fields/addCondition",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());

    if (!reportType) {
      throw new Error("Report type not initialized");
    }

    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("addCondition", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("addCondition", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("addCondition", payload));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("addCondition", payload));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("addCondition", payload));
    } else if (reportType === ReportType.FinancialStatement) {
      dispatch(createFinancialStatementAction("addCondition", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const removeCondition = createTypedAsyncThunk<void, ConditionField>(
  "fields/removeCondition",
  (field, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("removeCondition", field));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("removeCondition", field));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("removeCondition", field));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("removeCondition", field));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("removeCondition", field));
    } else if (reportType === ReportType.FinancialStatement) {
      dispatch(createFinancialStatementAction("removeCondition", field));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const moveCondition = createTypedAsyncThunk<void, { field: ConditionField; newIndex: number }>(
  "fields/moveCondition",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("moveCondition", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("moveCondition", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("moveCondition", payload));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("moveCondition", payload));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("moveCondition", payload));
    } else if (reportType === ReportType.FinancialStatement) {
      dispatch(createFinancialStatementAction("moveCondition", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateCondition = createTypedAsyncThunk<void, { field: ConditionField; changes: Partial<ConditionField> }>(
  "fields/updateCondition",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("updateCondition", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("updateCondition", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("updateCondition", payload));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("updateCondition", payload));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("updateCondition", payload));
    } else if (reportType === ReportType.FinancialStatement) {
      dispatch(createFinancialStatementAction("updateCondition", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateConditionConfig = createTypedAsyncThunk<
  void,
  { field: ConditionField; changes: Partial<ConditionConfiguration> }
>("fields/updateConditionConfig", (payload, { dispatch, getState }) => {
  const reportType = selectReportType(getState());
  if (!reportType) {
    throw new Error("Report type not initialized");
  }
  if (reportType === ReportType.Pivot) {
    dispatch(createPivotAction("updateConditionConfig", payload));
  } else if (usesTabularConfiguration(reportType)) {
    dispatch(createTabularAction("updateConditionConfig", payload));
  } else if (chartRelatedReportTypes.includes(reportType)) {
    dispatch(createChartAction("updateConditionConfig", payload));
  } else if (reportType === ReportType.Tile) {
    dispatch(createTileAction("updateConditionConfig", payload));
  } else if (reportType === ReportType.List) {
    dispatch(createListAction("updateConditionConfig", payload));
  } else if (reportType === ReportType.FinancialStatement) {
    dispatch(createFinancialStatementAction("updateConditionConfig", payload));
  } else {
    throw new Error("Report type not initialized");
  }
});

const setSorting = createTypedAsyncThunk<void, { fields: SortField[]; groupMetaNames: string[] }>(
  "fields/setSorting",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("setSorting", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("setSorting", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("setSorting", payload));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("setSorting", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const addSorting = createTypedAsyncThunk<void, SortableField>("fields/addSorting", (field, { dispatch, getState }) => {
  const reportType = selectReportType(getState());
  if (!reportType) {
    throw new Error("Report type not initialized");
  }
  if (reportType === ReportType.Pivot) {
    dispatch(createPivotAction("addSorting", field));
  } else if (usesTabularConfiguration(reportType)) {
    dispatch(createTabularAction("addSorting", field));
  } else if (chartRelatedReportTypes.includes(reportType)) {
    dispatch(createChartAction("addSorting", field));
  } else if (reportType === ReportType.List) {
    dispatch(createListAction("addSorting", field));
  } else {
    throw new Error("Report type not initialized");
  }
});

const removeSorting = createTypedAsyncThunk<void, SortField>(
  "fields/removeSorting",
  (field, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("removeSorting", field));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("removeSorting", field));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("removeSorting", field));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("removeSorting", field));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const moveSorting = createTypedAsyncThunk<void, { field: SortField; newIndex: number }>(
  "fields/moveSorting",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("moveSorting", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("moveSorting", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("moveSorting", payload));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("moveSorting", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateSortingConfig = createTypedAsyncThunk<void, { field: SortField; changes: Partial<SortConfiguration> }>(
  "fields/updateSortingConfig",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("updateSortingConfig", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("updateSortingConfig", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("updateSortingConfig", payload));
    } else if (reportType === ReportType.List) {
      dispatch(createListAction("updateSortingConfig", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const setSettings = createTypedAsyncThunk<
  void,
  {
    settings:
      | PivotGeneralSettings
      | TabularGeneralSettings
      | ChartStyleSettings
      | TileSettings
      | ListGeneralSettings
      | FinancialStatementGeneralSettings;
  }
>("fields/setSettings", (payload, { dispatch, getState }) => {
  const reportType = selectReportType(getState());
  if (!reportType) {
    throw new Error("Report type not initialized");
  }
  if (reportType === ReportType.Pivot) {
    dispatch(createPivotAction("setSettings", payload.settings as PivotGeneralSettings));
  } else if (usesTabularConfiguration(reportType)) {
    dispatch(createTabularAction("setSettings", payload.settings as TabularGeneralSettings));
  } else if (chartRelatedReportTypes.includes(reportType)) {
    dispatch(createChartAction("setSettings", payload.settings as Partial<ChartStyleSettings>));
  } else if (reportType === ReportType.Tile) {
    dispatch(createTileAction("setSettings", payload.settings as TileSettings));
  } else if (reportType === ReportType.List) {
    dispatch(createListAction("setSettings", payload.settings as ListGeneralSettings));
  } else if (reportType === ReportType.FinancialStatement) {
    dispatch(createFinancialStatementAction("setSettings", payload.settings as FinancialStatementGeneralSettings));
  } else {
    throw new Error("Report type not initialized");
  }
});

const updateSettings = createTypedAsyncThunk<
  void,
  {
    settings:
      | Partial<PivotGeneralSettings>
      | Partial<TabularGeneralSettings>
      | Partial<ChartStyleSettings>
      | Partial<TileSettings>
      | Partial<ListGeneralSettings>
      | Partial<FinancialStatementGeneralSettings>;
  }
>("fields/updateSettings", (payload, { dispatch, getState }) => {
  const reportType = selectReportType(getState());

  if (!reportType) {
    throw new Error("Report type not initialized");
  }
  if (reportType === ReportType.Pivot) {
    dispatch(createPivotAction("updateSettings", payload.settings as Partial<PivotGeneralSettings>));
  } else if (usesTabularConfiguration(reportType)) {
    dispatch(createTabularAction("updateSettings", payload.settings as Partial<TabularGeneralSettings>));
  } else if (chartRelatedReportTypes.includes(reportType)) {
    dispatch(createChartAction("updateSettings", payload.settings as Partial<ChartStyleSettings>));
  } else if (reportType === ReportType.Tile) {
    dispatch(createTileAction("updateSettings", payload.settings as Partial<TileSettings>));
  } else if (reportType === ReportType.List) {
    dispatch(createListAction("updateSettings", payload.settings as Partial<ListGeneralSettings>));
  } else if (reportType === ReportType.FinancialStatement) {
    dispatch(
      createFinancialStatementAction("updateSettings", payload.settings as Partial<FinancialStatementGeneralSettings>)
    );
  } else {
    throw new Error("Report type not initialized");
  }
});

const setMeasures = createTypedAsyncThunk<void, ReportField[]>(
  "fields/setMeasures",
  (measures, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("setMeasures", measures));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("setMeasures", measures));

      measures.forEach((field) => dispatch(createChartAction("addSeries", field.config.guid)));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

function initializeIrrPeriodDateField(field: ReportField, state: RootState): ReportField {
  if (field.config.type !== FieldConfigurationType.Irr || field.config.periodDateField) {
    return field;
  }
  const reportType = selectReportType(state);
  let dimensionFields: ReportField[] = [];
  if (reportType === ReportType.Pivot) {
    dimensionFields = [...selectRows(state), ...selectColumns(state)];
  } else if (usesTabularConfiguration(reportType)) {
    dimensionFields = selectTabularFields(state);
  }
  if (dimensionFields.length === 0) {
    return field;
  }
  const validity = isMeasureConfigurationValid(
    selectConditions(state),
    dimensionFields,
    selectDimensions(state),
    field
  );
  if (!validity.referenceDateField) {
    return field;
  }
  return { ...field, config: { ...field.config, periodDateField: validity.referenceDateField.name } };
}

const addMeasure = createTypedAsyncThunk<void, { field: ReportField; index: number }>(
  "fields/addMeasure",
  (payload, { dispatch, getState }) => {
    const state = getState();
    const reportType = selectReportType(state);
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    const finalPayload = { ...payload, field: initializeIrrPeriodDateField(payload.field, state) };
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("addMeasure", finalPayload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("addMeasure", finalPayload));

      dispatch(createChartAction("addSeries", finalPayload.field.config.guid));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const removeMeasure = createTypedAsyncThunk<void, ReportField>(
  "fields/removeMeasure",
  (field, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("removeMeasure", field));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("removeMeasure", field));

      dispatch(createChartAction("removeSeries", field.config.guid));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const moveMeasure = createTypedAsyncThunk<void, { field: ReportField; newIndex: number }>(
  "fields/moveMeasure",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("moveMeasure", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("moveMeasure", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateMeasure = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<ReportField> }>(
  "fields/updateMeasure",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("updateMeasure", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("updateMeasure", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateMeasureConfig = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<FieldConfiguration> }>(
  "fields/updateMeasureConfig",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("updateMeasureConfig", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("updateMeasureConfig", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateFieldConfig = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<FieldConfiguration> }>(
  "fields/updateFieldConfig",
  (payload: { field: ReportField; changes: Partial<FieldConfiguration> }, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("updateFieldConfig", payload));
    } else if (isListReportType(reportType)) {
      dispatch(createListAction("updateFieldConfig", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(tabularFieldsStateThunks.updateTabularFieldConfig(payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateMeasureField = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<ReportField> }>(
  "fields/updateMeasureField",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("updateMeasureField", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("updateMeasureField", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateFieldsOrder = createTypedAsyncThunk<void, string[]>(
  "fields/updateFieldsOrder",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());

    if (isTileReportType(reportType)) {
      dispatch(createTileAction("updateFieldsOrder", payload));
    } else if (isListReportType(reportType)) {
      dispatch(createListAction("updateFieldsOrder", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("updateFieldsOrder", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const moveField = createTypedAsyncThunk<void, { field: ReportField; newIndex: number }>(
  "fields/moveField",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("moveField", payload));
    } else if (isListReportType(reportType)) {
      dispatch(createListAction("moveField", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("moveField", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const removeField = createTypedAsyncThunk<void, ReportField>(
  "fields/removeField",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("removeField", payload));
    } else if (isListReportType(reportType)) {
      dispatch(createListAction("removeField", payload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("removeField", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const addField = createTypedAsyncThunk<void, { field: ReportField; index: number }>(
  "fields/addField",
  (payload, { dispatch, getState }) => {
    const state = getState();
    const reportType = selectReportType(state);
    const finalPayload = { ...payload, field: initializeIrrPeriodDateField(payload.field, state) };
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("addField", finalPayload));
    } else if (isListReportType(reportType)) {
      dispatch(createListAction("addField", finalPayload));
    } else if (usesTabularConfiguration(reportType)) {
      dispatch(createTabularAction("addField", finalPayload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const setFields = createTypedAsyncThunk<void, ReportField[]>("fields/setFields", (payload, { dispatch, getState }) => {
  const reportType = selectReportType(getState());
  if (isTileReportType(reportType)) {
    dispatch(createTileAction("setFields", payload));
  } else if (isListReportType(reportType)) {
    dispatch(createListAction("setFields", payload));
  } else if (usesTabularConfiguration(reportType)) {
    dispatch(createTabularAction("setFields", payload));
  } else {
    throw new Error("Report type not initialized");
  }
});

const setLedger = createTypedAsyncThunk<void, string | undefined>("fields/setLedger", (payload, { dispatch }) => {
  dispatch(createListAction("setLedger", payload));
});

const changeLedger = createTypedAsyncThunk<void, string | undefined>(
  "fields/changeLedger",
  (payload, { dispatch, getState }) => {
    const change = createListLedgerChange(selectListFieldsState(getState()), payload);
    dispatch(createListAction("applyLedgerChange", change));
    dispatch(createCurrentReportAction("refresh"));
  }
);

const fieldStateThunks = {
  initializeFieldsState,
  handleReportTypeChange,
  clearCurrentConfiguration,
  setConditions,
  addCondition,
  removeCondition,
  moveCondition,
  updateCondition,
  updateConditionConfig,
  setSorting,
  addSorting,
  removeSorting,
  moveSorting,
  updateSortingConfig,
  setSettings,
  updateSettings,
  setMeasures,
  addMeasure,
  removeMeasure,
  moveMeasure,
  updateMeasure,
  updateMeasureConfig,
  updateFieldConfig,
  updateMeasureField,
  resetFieldsState,
  updateFieldsOrder,
  moveField,
  removeField,
  addField,
  setFields,
  setLedger,
  changeLedger,
};

export const fieldsStateActions = {
  ...fieldStateThunks,
  // Type to ensure no duplicate keys between objects
  ...(tabularFieldsStateThunks as NoDuplicateKeys<typeof tabularFieldsStateThunks, typeof pivotFieldsStateThunks>),
  ...(pivotFieldsStateThunks as NoDuplicateKeys<typeof pivotFieldsStateThunks, typeof tabularFieldsStateThunks>),
  ...(chartFieldsStateThunks as NoDuplicateKeys<typeof chartFieldsStateThunks, typeof pivotFieldsStateThunks>),
};
